'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "classes", "sx"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ComposerSendButton } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { mergeSlotProps } from "../internals/mergeSlotProps.mjs";
import { useChatComposerUtilityClasses } from "./chatComposerClasses.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatComposerSendButton');
const ChatComposerSendButtonStyled = styled('button', {
  name: 'MuiChatComposer',
  slot: 'SendButton',
  overridesResolver: (_, styles) => styles.sendButton
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: (theme.vars || theme).palette.primary.main,
  color: (theme.vars || theme).palette.primary.contrastText,
  cursor: 'pointer',
  flexShrink: 0,
  marginInlineStart: 'auto',
  transition: theme.transitions.create(['background-color', 'opacity'], {
    duration: theme.transitions.duration.short
  }),
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none'
  },
  padding: 0,
  fontSize: '1.25rem',
  '&:hover': {
    backgroundColor: (theme.vars || theme).palette.primary.dark
  },
  '&:focus-visible': {
    outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: 2
  },
  '&:disabled': {
    backgroundColor: (theme.vars || theme).palette.action.disabledBackground,
    color: (theme.vars || theme).palette.action.disabled,
    cursor: 'not-allowed',
    opacity: (theme.vars || theme).palette.action.disabledOpacity
  },
  '& svg': {
    width: '1em',
    height: '1em',
    fontSize: 'inherit'
  }
}));
const ChatComposerSendButton = /*#__PURE__*/React.forwardRef(function ChatComposerSendButton(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatComposerSendButton'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatComposerUtilityClasses(classesProp);
  return /*#__PURE__*/_jsx(ComposerSendButton, _extends({
    ref: ref
  }, other, {
    slots: _extends({}, slots, {
      sendButton: slots?.sendButton ?? ChatComposerSendButtonStyled
    }),
    slotProps: _extends({}, slotProps, {
      sendButton: mergeSlotProps({
        className: clsx(classes.sendButton, className),
        sx
      }, slotProps?.sendButton)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatComposerSendButton.displayName = "ChatComposerSendButton";
process.env.NODE_ENV !== "production" ? ChatComposerSendButton.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatComposerSendButton };